/**
 * TrendCharts component for visualizing wellness metrics over time.
 *
 * Shows line charts for sleep quality, energy level, and mood using Recharts.
 * Responsive design for mobile devices.
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { TrendData } from '../../types';

interface TrendChartsProps {
  sleepData: TrendData[];
  energyData: TrendData[];
  moodData: TrendData[];
}

const TrendCharts: React.FC<TrendChartsProps> = ({ sleepData, energyData, moodData }) => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatSleepData = sleepData.map(item => ({
    date: formatDate(item.date),
    value: item.value
  }));

  const formatEnergyData = energyData.map(item => ({
    date: formatDate(item.date),
    value: item.value
  }));

  const formatMoodData = moodData.map(item => ({
    date: formatDate(item.date),
    value: item.value
  }));

  if (!sleepData.length && !energyData.length && !moodData.length) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <p className="text-gray-600">No trend data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-8">
      <h3 className="text-lg font-semibold text-gray-900">Wellness Trends</h3>

      {/* Sleep Quality Chart */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Sleep Quality (1-5 scale)</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={formatSleepData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, 5]}
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem'
              }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={{ fill: '#8b5cf6', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Energy Level Chart */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Energy Level</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={formatEnergyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              ticks={[0, 33, 66, 100]}
              tickFormatter={(value) => {
                if (value === 0) return 'Low';
                if (value === 33) return 'Low';
                if (value === 66) return 'Med';
                if (value === 100) return 'High';
                return '';
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem'
              }}
              formatter={(value: any) => {
                if (value >= 90) return 'High';
                if (value >= 50) return 'Medium';
                return 'Low';
              }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Mood Chart */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Mood</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={formatMoodData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(value) => {
                if (value === 0) return 'V.Low';
                if (value === 25) return 'Low';
                if (value === 50) return 'Neutral';
                if (value === 75) return 'Good';
                if (value === 100) return 'Great';
                return '';
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem'
              }}
              formatter={(value: any) => {
                if (value >= 90) return 'Great';
                if (value >= 65) return 'Good';
                if (value >= 40) return 'Neutral';
                if (value >= 15) return 'Low';
                return 'Very Low';
              }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#ec4899"
              strokeWidth={2}
              dot={{ fill: '#ec4899', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-gray-500">
        Charts show your wellness metrics over time. Look for patterns and trends.
      </p>
    </div>
  );
};

export default TrendCharts;
